import { ApiError } from "@/lib/vibrato-api"
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "flowbite-react"
import { NavArrowLeft, NavArrowRight, Phone, Search, InfoCircle, Check } from "iconoir-react"
import React, { useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { VibratoClientContext } from "./contexts"

function CreateTwilioPhoneNumberModal({ show, onClose }: { show: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const vibratoClient = React.useContext(VibratoClientContext)
  const formRef = useRef<HTMLDivElement>(null)
  const [message, setMessage] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [areaCode, setAreaCode] = useState("")
  const [beginsWithDigits, setBeginsWithDigits] = useState("")
  const [numberBeingCreated, setNumberBeingCreated] = useState<string | null>(null)
  const [isFetchingAvailableNumbers, setIsFetchingAvailableNumbers] = useState(false)
  const [availableNumbers, setAvailableNumbers] = useState<any[] | null>(null)
  const [disableButtons, setDisableButtons] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  const onCreateTwilioPhoneNumber = async (phoneNumber: string) => {
    setDisableButtons(true)
    setNumberBeingCreated(phoneNumber)
    setErrorMessage("")
    setMessage("")

    if (vibratoClient) {
      try {
        const createdTwilioPhoneNumber = await vibratoClient.createTwilioPhoneNumber(phoneNumber)
        setMessage("Added phone number successfully!")
        await new Promise((resolve) => setTimeout(resolve, 1000))
        navigate(`/phone-numbers/${createdTwilioPhoneNumber.uuid}`)
      } catch (error) {
        if (error instanceof ApiError) {
          setErrorMessage(`Error adding phone number: ${JSON.stringify(error.data)}`)
        } else {
          setErrorMessage("Error adding phone number")
        }
      }
    }
    setDisableButtons(false)
  }

  const onListAvailableTwilioPhoneNumbers = async () => {
    setIsFetchingAvailableNumbers(true)
    setErrorMessage("")
    setAvailableNumbers(null)

    if (vibratoClient) {
      try {
        // Always use "US" as the country code
        const fetchedAvailableNumbers = await vibratoClient.getAvailableTwilioPhoneNumbers(
          "US",
          areaCode,
          beginsWithDigits
        )
        setAvailableNumbers(fetchedAvailableNumbers)
        setTimeout(() => {
          formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
        }, 100)
      } catch (error) {
        if (error instanceof ApiError) {
          setErrorMessage(`Error searching for phone numbers: ${JSON.stringify(error.data)}`)
        } else {
          setErrorMessage("Error fetching available phone numbers")
        }
      }
    }
    setIsFetchingAvailableNumbers(false)
  }

  const renderIntroPage = () => (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col space-y-6">
        <div className="bg-linear-to-r from-purple-50 to-blue-50 dark:from-purple-900/10 dark:to-blue-900/10 p-6 rounded-xl">
          <h3 className="text-xl font-bold text-transparent bg-clip-text bg-linear-to-r from-purple-600 to-blue-500 mb-4">
            Add a Phone Number
          </h3>
          <p className="text-gray-700 dark:text-gray-300 text-lg mb-4">
            Get your own dedicated phone number that you can use for all your AI calling needs:
          </p>
          <ul className="space-y-3 text-gray-700 dark:text-gray-300">
            <li className="flex items-center gap-2">
              <div className="shrink-0 w-5 h-5 rounded-full bg-linear-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
              <span>Use as your custom caller ID for outbound calls</span>
            </li>
            <li className="flex items-center gap-2">
              <div className="shrink-0 w-5 h-5 rounded-full bg-linear-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
              <span>Receive callbacks to your personal number</span>
            </li>
            <li className="flex items-center gap-2">
              <div className="shrink-0 w-5 h-5 rounded-full bg-linear-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
              <span>Automate inbound calls with task templates</span>
            </li>
            <li className="flex items-center gap-2">
              <div className="shrink-0 w-5 h-5 rounded-full bg-linear-to-r from-purple-500 to-blue-500 flex items-center justify-center">
                <Check className="w-3 h-3 text-white" />
              </div>
              <span>Keep your personal number private</span>
            </li>
          </ul>
        </div>

        <div className="flex items-center p-4 bg-amber-50 border-l-4 border-amber-400 rounded-lg">
          <InfoCircle className="w-5 h-5 text-amber-500 mr-3 shrink-0" />
          <p className="text-amber-700 text-sm">
            Currently only US phone numbers (country code +1) are supported. For international numbers, please contact
            support.
          </p>
        </div>
      </div>
    </div>
  )

  const renderSearchPage = () => (
    <div className="flex flex-col gap-6">
      <div className="bg-linear-to-r from-purple-50 to-blue-50 p-6 rounded-xl">
        <h3 className="text-xl font-bold text-transparent bg-clip-text bg-linear-to-r from-purple-600 to-blue-500 mb-4">
          Find Your Perfect Number
        </h3>
        <p className="text-gray-700 mb-6">
          Search for available US phone numbers by specifying area code or beginning digits.
        </p>

        <div className="flex flex-col gap-5" ref={formRef}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="areaCode" className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <span>Area Code</span>
                </div>
              </label>
              <input
                type="text"
                id="areaCode"
                value={areaCode}
                onChange={(e) => setAreaCode(e.target.value)}
                className="py-3 px-4 block w-full border-gray-200 rounded-lg text-base focus:border-purple-500 focus:ring-purple-500 dark:bg-slate-900 dark:border-gray-700 dark:text-gray-300"
                placeholder="415"
                maxLength={3}
                required
              />
            </div>

            <div>
              <label htmlFor="beginsWithDigits" className="block text-sm font-medium text-gray-700 mb-1">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <span>Begins With</span>
                </div>
              </label>
              <input
                type="tel"
                id="beginsWithDigits"
                pattern="[0-9]+"
                maxLength={7}
                value={beginsWithDigits}
                onChange={(e) => setBeginsWithDigits(e.target.value)}
                placeholder="e.g. 555"
                className="py-3 px-4 block w-full border-gray-200 rounded-lg text-base focus:border-purple-500 focus:ring-purple-500 dark:bg-slate-900 dark:border-gray-700 dark:text-gray-300"
              />
            </div>
          </div>

          {errorMessage && (
            <div className="p-4 bg-red-50 border-l-4 border-red-400 rounded-lg">
              <p className="text-red-700 text-sm">{errorMessage}</p>
            </div>
          )}

          <div>
            <button
              type="button"
              onClick={onListAvailableTwilioPhoneNumbers}
              className="w-full bg-linear-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
              disabled={isFetchingAvailableNumbers}
            >
              {isFetchingAvailableNumbers ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <span>Searching...</span>
                </>
              ) : (
                <>
                  <Search className="w-5 h-5" />
                  <span>Search Available Numbers</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {availableNumbers !== null && (
        <div className="mt-2">
          <div className="h-px bg-linear-to-r from-purple-200 via-blue-200 to-purple-200 dark:from-purple-900 dark:via-blue-900 dark:to-purple-900 mb-4" />
          <h3 className="text-lg font-bold text-transparent bg-clip-text bg-linear-to-r from-purple-600 to-blue-500 mb-3">
            Available Phone Numbers
          </h3>
          <div className="overflow-hidden">
            {availableNumbers.length > 0 ? (
              <div className="rounded-xl border border-gray-200 shadow-sm overflow-hidden dark:border-gray-700">
                <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                    <tr>
                      <th
                        scope="col"
                        className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                      >
                        Phone Number
                      </th>
                      <th
                        scope="col"
                        className="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                      >
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                    {availableNumbers.map((availableNumber) => (
                      <tr
                        key={availableNumber.phone_number}
                        className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors duration-150"
                      >
                        <td className="px-3 py-3">
                          <div className="space-y-2">
                            <div className="flex items-center">
                              <div className="bg-blue-100 dark:bg-blue-900 rounded-full p-1.5 mr-2">
                                <Phone className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {availableNumber.friendly_name}
                                </div>
                              </div>
                            </div>
                            {errorMessage && numberBeingCreated === availableNumber.phone_number && (
                              <div className="px-3 py-2 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 rounded">
                                <p className="text-red-700 dark:text-red-400 text-xs">{errorMessage}</p>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right text-sm font-medium">
                          <div className="flex justify-end">
                            <button
                              type="button"
                              onClick={() => onCreateTwilioPhoneNumber(availableNumber.phone_number)}
                              className="bg-linear-to-r from-purple-600 to-blue-500 hover:from-purple-700 hover:to-blue-600 text-white font-medium py-1.5 px-4 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={disableButtons}
                            >
                              {numberBeingCreated === availableNumber.phone_number && !errorMessage ? (
                                <span className="flex items-center gap-2">
                                  <svg
                                    className="animate-spin h-4 w-4"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                  >
                                    <circle
                                      className="opacity-25"
                                      cx="12"
                                      cy="12"
                                      r="10"
                                      stroke="currentColor"
                                      strokeWidth="4"
                                    />
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    />
                                  </svg>
                                  Adding...
                                </span>
                              ) : message && numberBeingCreated === availableNumber.phone_number ? (
                                <span className="flex items-center gap-2">
                                  <Check className="w-4 h-4" />
                                  Added
                                </span>
                              ) : (
                                "Add"
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex justify-center items-center h-[200px] bg-gray-50 dark:bg-gray-800/50">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400 mb-2">
                    <Phone className="w-5 h-5" />
                  </div>
                  <h3 className="mt-2 text-base font-medium text-gray-900 dark:text-gray-100">
                    No phone numbers available
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Try different search criteria</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )

  return (
    <Modal show={show} onClose={onClose} size="xl" className="pt-safe">
      <ModalHeader className="border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded-full">
            <Phone className="w-5 h-5 text-purple-600" />
          </div>
          <span className="text-transparent bg-clip-text bg-linear-to-r from-purple-600 to-blue-500 text-xl font-bold">
            Add Phone Number
          </span>
        </div>
      </ModalHeader>
      <ModalBody>{currentPage === 1 ? renderIntroPage() : renderSearchPage()}</ModalBody>
      <ModalFooter>
        <div className="flex justify-between w-full">
          {currentPage > 1 && (
            <Button color="gray" onClick={() => setCurrentPage(1)} className="flex items-center gap-2">
              <NavArrowLeft className="w-5 h-5" />
              Back
            </Button>
          )}
          {currentPage === 1 && (
            <Button color="blue" onClick={() => setCurrentPage(2)} className="flex items-center gap-2 ml-auto">
              Continue
              <NavArrowRight className="w-5 h-5" />
            </Button>
          )}
        </div>
      </ModalFooter>
    </Modal>
  )
}

export default CreateTwilioPhoneNumberModal
